/**
 * 紙風カード内に問診票を描画するコンポーネント。
 * dental-avatar-design/handoff/questionnaire/ のデザインをTypeScript移植。
 */
import { createContext, useContext, type ReactNode } from "react";

// ==================== 型 ====================
export interface QuestionnaireJsonData {
  complaint1?: {
    symptoms?: string[];
    other?: string;
  };
  complaint2?: {
    diseases?: string[];
    heart_details?: string[];
    other?: string;
  };
  complaint3?: {
    has_medication?: "yes" | "none";
    medication_name?: string;
    has_medication_book?: boolean;
    has_medication_item?: boolean;
  };
  complaint4?: {
    bad_feeling?: "yes" | "no";
    allergy?: "yes" | "suspected" | "no";
    pregnancy?: "yes" | "suspected" | "no";
    other?: string;
  };
  complaint5?: {
    brushing?: {
      times?: string[];
      duration?: number;
      tools?: string[];
      tools_other?: string;
    };
    smoking?: "none" | "yes" | "past";
    sleep_hours?: number;
    drinks?: {
      has_drinks?: "none" | "yes";
      name?: string;
    };
    snacking?: "none" | "regular" | "irregular";
  };
  complaint6?: {
    treatments?: string[];
  };
}

// ==================== 定数 ====================
const Q_COLORS = {
  paper: "#FDFCF7",
  paperDeep: "#F6F1E2",
  paperLine: "#ECE5D4",
  paperLineSoft: "#EFE9D8",
  ink: "#2A261E",
  inkMute: "#6B6144",
  inkFade: "#A1997E",
  accent: "#8A7E5F",
  checkFill: "#2A261E",
};

const Q_FONT =
  '"Hiragino Sans", "Hiragino Kaku Gothic ProN", "Noto Sans JP", system-ui, sans-serif';

function formatBirthDateForDisplay(value?: string | null): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(trimmed);
  if (iso) return `${iso[1]}年${Number(iso[2])}月${Number(iso[3])}日`;
  return trimmed;
}

const DISEASE_LABELS: Record<string, string> = {
  none: "ない",
  hypertension: "高血圧",
  heart_disease: "心臓病",
  diabetes: "糖尿病",
  stroke: "脳卒中",
  asthma: "ぜんそく",
  liver_disease: "肝臓病",
  kidney_disease: "腎臓病",
  epilepsy: "てんかん",
  allergy: "アレルギー",
  thyroid: "甲状腺の病気",
  cancer: "がん",
  dementia: "認知症",
  hiv: "HIV",
};
const DISEASE_ORDER = [
  "none", "hypertension", "heart_disease", "diabetes", "stroke", "asthma",
  "liver_disease", "kidney_disease", "epilepsy", "allergy", "thyroid", "cancer", "dementia", "hiv",
];
const HEART_DETAIL_LIST = ["狭心症", "心筋梗塞", "不整脈", "弁膜症", "心不全", "心筋症", "先天性心疾患"];
const SYMPTOM_LIST = [
  "歯が痛い", "虫歯がある", "歯ぐきが腫れた", "歯石が気になる",
  "詰め物が取れた", "義歯が壊れた", "歯を入れてもらいたい",
];
const BRUSHING_TIMES = [
  { key: "morning", label: "起床後" },
  { key: "after_breakfast", label: "朝食後" },
  { key: "after_lunch", label: "昼食後" },
  { key: "after_dinner", label: "夕食後" },
  { key: "before_bed", label: "就寝前" },
];
const BRUSHING_TOOLS = [
  { key: "toothbrush", label: "歯ブラシ" },
  { key: "floss", label: "フロス" },
  { key: "interdental", label: "歯間ブラシ" },
];
const TREATMENT_LIST = [
  "悪いところは全部治療したい",
  "痛いところだけ、希望するところだけ治したい",
  "歯の磨き方などについても教えて欲しい",
];

// ==================== ヘルパ ====================
const has = (arr: unknown, v: string): boolean => Array.isArray(arr) && arr.includes(v);
const hasText = (v: unknown): boolean => v != null && String(v).trim().length > 0;
const QuestionnaireCompactContext = createContext(false);
const useQuestionnaireCompact = () => useContext(QuestionnaireCompactContext);

// ==================== Atoms ====================
function Box({ checked }: { checked: boolean }) {
  const compact = useQuestionnaireCompact();
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: compact ? 14 : 15,
        height: compact ? 14 : 15,
        flexShrink: 0,
        border: `1.3px solid ${checked ? Q_COLORS.checkFill : Q_COLORS.inkFade}`,
        borderRadius: 2,
        background: checked ? Q_COLORS.checkFill : "transparent",
        color: "#FDFCF7",
        fontSize: 11,
        fontWeight: 900,
        lineHeight: 1,
        marginTop: compact ? 3 : -1,
      }}
    >
      {checked ? "✓" : ""}
    </span>
  );
}

function CheckChip({
  checked,
  children,
  strong,
}: {
  checked: boolean;
  children: ReactNode;
  strong?: boolean;
}) {
  const compact = useQuestionnaireCompact();
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: compact ? "flex-start" : "center",
        gap: 6,
        whiteSpace: compact ? "normal" : "nowrap",
        fontSize: compact ? 13 : 14,
        lineHeight: 1.6,
        color: checked ? Q_COLORS.ink : Q_COLORS.inkMute,
        fontWeight: checked ? (strong ? 700 : 600) : 400,
        padding: compact ? "2px 8px 2px 2px" : "2px 10px 2px 2px",
        letterSpacing: "0.02em",
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      <Box checked={checked} />
      <span style={{ minWidth: 0 }}>{children}</span>
    </span>
  );
}

function FillLine({
  value,
  placeholder,
  variant = "inline",
  width,
}: {
  value?: unknown;
  placeholder?: string;
  variant?: "inline" | "full";
  width?: number;
}) {
  const compact = useQuestionnaireCompact();
  const hasValue = value != null && String(value).trim().length > 0;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "flex-end",
        verticalAlign: "bottom",
        width: variant === "full" ? "100%" : compact ? "100%" : width ?? 220,
        minHeight: 26,
        padding: "2px 8px 3px",
        borderBottom: `1px dashed ${Q_COLORS.paperLine}`,
        background: hasValue ? "#FBF7EA" : "transparent",
        fontSize: 14,
        lineHeight: 1.6,
        color: hasValue ? Q_COLORS.ink : Q_COLORS.inkFade,
        fontStyle: hasValue ? "normal" : "italic",
        fontWeight: hasValue ? 500 : 400,
        letterSpacing: "0.02em",
        wordBreak: "break-word",
        whiteSpace: "normal",
        boxSizing: "border-box",
      }}
    >
      {hasValue ? String(value) : placeholder ?? "\u00A0"}
    </span>
  );
}

function Row({
  label,
  children,
  labelCol = 170,
  dense,
}: {
  label: ReactNode;
  children: ReactNode;
  labelCol?: number;
  dense?: boolean;
}) {
  const compact = useQuestionnaireCompact();
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: compact ? "1fr" : `${labelCol}px 1fr`,
        gap: compact ? 4 : 16,
        alignItems: "baseline",
        padding: dense ? "6px 0" : compact ? "8px 0" : "10px 0",
        borderTop: `1px dotted ${Q_COLORS.paperLineSoft}`,
      }}
    >
      <div
        style={{
          fontSize: compact ? 12 : 13,
          fontWeight: 600,
          color: Q_COLORS.inkMute,
          letterSpacing: "0.04em",
          paddingTop: 2,
        }}
      >
        {label}
      </div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "baseline",
          gap: "6px 4px",
          minHeight: 26,
          minWidth: 0,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function NumItem({
  num,
  label,
  children,
  labelCol = 170,
}: {
  num: string;
  label: ReactNode;
  children: ReactNode;
  labelCol?: number;
}) {
  const compact = useQuestionnaireCompact();
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: compact ? "22px 1fr" : `28px ${labelCol - 28}px 1fr`,
        gap: compact ? "4px 8px" : "0 12px",
        alignItems: "baseline",
        padding: compact ? "9px 0" : "8px 0",
        borderTop: `1px dotted ${Q_COLORS.paperLineSoft}`,
      }}
    >
      <div
        style={{
          fontSize: compact ? 12 : 13,
          fontWeight: 700,
          color: Q_COLORS.accent,
          textAlign: "right",
          paddingRight: 4,
        }}
      >
        {num}
      </div>
      <div
        style={{
          fontSize: compact ? 12.5 : 13,
          fontWeight: 600,
          color: Q_COLORS.inkMute,
          letterSpacing: "0.04em",
        }}
      >
        {label}
      </div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "baseline",
          gap: "6px 4px",
          minHeight: 26,
          minWidth: 0,
          gridColumn: compact ? "1 / -1" : undefined,
          paddingLeft: compact ? 22 : undefined,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function SectionHead({
  num,
  title,
  note,
}: {
  num: string;
  title: string;
  note?: string;
}) {
  const compact = useQuestionnaireCompact();
  return (
    <div
      style={{
        display: "flex",
        alignItems: compact ? "flex-start" : "baseline",
        flexWrap: "wrap",
        gap: compact ? "6px 8px" : 10,
        padding: compact ? "16px 0 9px" : "18px 0 10px",
        marginTop: 8,
        borderTop: `1px solid ${Q_COLORS.paperLine}`,
        borderBottom: `1px solid ${Q_COLORS.paperLine}`,
        marginBottom: 4,
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: compact ? 24 : 28,
          height: compact ? 22 : 24,
          padding: compact ? "0 7px" : "0 8px",
          borderRadius: 4,
          background: Q_COLORS.checkFill,
          color: Q_COLORS.paper,
          fontSize: compact ? 11 : 12,
          fontWeight: 800,
          letterSpacing: "0.08em",
        }}
      >
        [{num}]
      </span>
      <span
        style={{
          fontSize: compact ? 14 : 15,
          fontWeight: 800,
          color: Q_COLORS.ink,
          letterSpacing: "0.04em",
        }}
      >
        {title}
      </span>
      {note && (
        <span
          style={{
            fontSize: compact ? 10.5 : 11,
            color: Q_COLORS.inkFade,
            fontWeight: 500,
            letterSpacing: "0.04em",
          }}
        >
          {note}
        </span>
      )}
    </div>
  );
}

function SubFillRow({
  label,
  value,
  placeholder,
}: {
  label: ReactNode;
  value?: string;
  placeholder?: string;
}) {
  const compact = useQuestionnaireCompact();
  return (
    <div
      style={{
        gridColumn: "1 / -1",
        width: "100%",
        display: "grid",
        gridTemplateColumns: compact ? "1fr" : "auto 1fr",
        gap: compact ? 4 : 10,
        alignItems: "baseline",
        marginTop: 4,
        marginLeft: compact ? 0 : 24,
      }}
    >
      <span style={{ fontSize: 12, color: Q_COLORS.inkMute, whiteSpace: compact ? "normal" : "nowrap" }}>
        {label}
      </span>
      <FillLine value={value} placeholder={placeholder} variant="full" />
    </div>
  );
}

function OtherRow({
  label,
  checked,
  value,
}: {
  label: string;
  checked: boolean;
  value?: string;
}) {
  const compact = useQuestionnaireCompact();
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: compact ? "1fr" : "170px 1fr",
        gap: compact ? 5 : 16,
        alignItems: "baseline",
        padding: "8px 0",
        borderTop: `1px dotted ${Q_COLORS.paperLineSoft}`,
      }}
    >
      <div>
        <CheckChip checked={checked}>{label}</CheckChip>
      </div>
      <div>
        <FillLine
          value={value}
          placeholder=" "
          variant="full"
        />
      </div>
    </div>
  );
}

// ==================== Sections ====================
function Section1({ data }: { data?: QuestionnaireJsonData["complaint1"] }) {
  const symptoms = data?.symptoms ?? [];
  const other = data?.other ?? "";
  return (
    <>
      <SectionHead num="１" title="本日はいかがされましたか？" note="あてはまるものすべて" />
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 6px", padding: "10px 0" }}>
        {SYMPTOM_LIST.map((s) => (
          <CheckChip key={s} checked={has(symptoms, s)}>{s}</CheckChip>
        ))}
      </div>
      <OtherRow label="その他" checked={hasText(other)} value={other} />
    </>
  );
}

function Section2({ data }: { data?: QuestionnaireJsonData["complaint2"] }) {
  const compact = useQuestionnaireCompact();
  const diseases = data?.diseases ?? [];
  const heart_details = data?.heart_details ?? [];
  const other = data?.other ?? "";
  const heartChecked = has(diseases, "heart_disease");
  return (
    <>
      <SectionHead num="２" title="通院中または治療中の病気はありますか？" />
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 6px", padding: "10px 0" }}>
        {DISEASE_ORDER.map((k) => (
          <CheckChip key={k} checked={has(diseases, k)} strong={k === "none"}>
            {DISEASE_LABELS[k]}
          </CheckChip>
        ))}
      </div>
      {heartChecked && (
        <div
          style={{
            marginLeft: compact ? 0 : 24,
            padding: "10px 12px 8px",
            background: "#F9F4E3",
            border: `1px dashed ${Q_COLORS.paperLine}`,
            borderRadius: 6,
          }}
        >
          <div
            style={{
              fontSize: 11,
              color: Q_COLORS.accent,
              fontWeight: 700,
              marginBottom: 4,
              letterSpacing: "0.08em",
            }}
          >
            └ 心臓病の詳細
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 6px" }}>
            {HEART_DETAIL_LIST.map((d) => (
              <CheckChip key={d} checked={has(heart_details, d)}>{d}</CheckChip>
            ))}
          </div>
        </div>
      )}
      <OtherRow label="その他" checked={hasText(other)} value={other} />
    </>
  );
}

function Section3({ data }: { data?: QuestionnaireJsonData["complaint3"] }) {
  const d = data ?? {};
  const isYes = d.has_medication === "yes";
  return (
    <>
      <SectionHead num="３" title="服用中のお薬はありますか？" />
      <Row label="服用の有無">
        <CheckChip checked={d.has_medication === "none"}>ない</CheckChip>
        <CheckChip checked={isYes}>ある</CheckChip>
      </Row>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "170px 1fr",
          gap: 16,
          alignItems: "baseline",
          padding: "8px 0",
          borderTop: `1px dotted ${Q_COLORS.paperLineSoft}`,
          opacity: isYes ? 1 : 0.45,
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 600, color: Q_COLORS.inkMute, paddingTop: 2 }}>
          お薬の名前
        </div>
        <FillLine
          value={isYes ? d.medication_name : ""}
          placeholder={isYes ? "（記入なし）" : "（該当なし）"}
          variant="full"
        />
      </div>
      <Row label="持参">
        <CheckChip checked={d.has_medication_book === true}>お薬手帳あり</CheckChip>
        <CheckChip checked={d.has_medication_item === true}>お薬そのもの あり</CheckChip>
      </Row>
    </>
  );
}

function Section4({ data }: { data?: QuestionnaireJsonData["complaint4"] }) {
  const d = data ?? {};
  return (
    <>
      <SectionHead num="４" title="気になるお身体の状態" />
      <NumItem num="①" label="歯科治療中に気分が悪くなったことがある">
        <CheckChip checked={d.bad_feeling === "no"}>ない</CheckChip>
        <CheckChip checked={d.bad_feeling === "yes"}>ある</CheckChip>
      </NumItem>
      <NumItem num="②" label="薬のアレルギー">
        <CheckChip checked={d.allergy === "no"}>ない</CheckChip>
        <CheckChip checked={d.allergy === "suspected"}>疑いがある</CheckChip>
        <CheckChip checked={d.allergy === "yes"}>ある</CheckChip>
      </NumItem>
      <NumItem num="③" label="妊娠・授乳中">
        <CheckChip checked={d.pregnancy === "no"}>該当なし</CheckChip>
        <CheckChip checked={d.pregnancy === "suspected"}>可能性あり</CheckChip>
        <CheckChip checked={d.pregnancy === "yes"}>該当する</CheckChip>
      </NumItem>
      <OtherRow label="その他 気になること" checked={hasText(d.other)} value={d.other} />
    </>
  );
}

function Section5({ data }: { data?: QuestionnaireJsonData["complaint5"] }) {
  const compact = useQuestionnaireCompact();
  const d = data ?? {};
  const b = d.brushing ?? {};
  const dr = d.drinks ?? {};
  const drinksYes = dr.has_drinks === "yes";
  return (
    <>
      <SectionHead num="５" title="生活習慣について" />

      {/* ① 歯磨き */}
      <NumItem num="①" label="歯磨き">
        {BRUSHING_TIMES.map((t) => (
          <CheckChip key={t.key} checked={has(b.times, t.key)}>{t.label}</CheckChip>
        ))}
      </NumItem>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: compact ? "22px 1fr" : "28px 140px 1fr",
          gap: compact ? "4px 8px" : "0 12px",
          alignItems: "baseline",
          padding: "6px 0",
        }}
      >
        <div />
        <div style={{ fontSize: 12, color: Q_COLORS.inkMute, paddingLeft: 4 }}>1回の時間</div>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 6,
            gridColumn: compact ? "1 / -1" : undefined,
            paddingLeft: compact ? 22 : undefined,
          }}
        >
          <FillLine
            value={b.duration != null && (b.duration as unknown) !== "" ? `${b.duration} 分` : ""}
            placeholder="—"
            width={120}
          />
        </div>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: compact ? "22px 1fr" : "28px 140px 1fr",
          gap: compact ? "4px 8px" : "0 12px",
          alignItems: "baseline",
          padding: "6px 0",
        }}
      >
        <div />
        <div style={{ fontSize: 12, color: Q_COLORS.inkMute, paddingLeft: 4 }}>使う道具</div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "baseline",
            gap: "4px 6px",
            gridColumn: compact ? "1 / -1" : undefined,
            paddingLeft: compact ? 22 : undefined,
          }}
        >
          {BRUSHING_TOOLS.map((t) => (
            <CheckChip key={t.key} checked={has(b.tools, t.key)}>{t.label}</CheckChip>
          ))}
          <CheckChip checked={hasText(b.tools_other)}>その他</CheckChip>
          {hasText(b.tools_other) && (
            <SubFillRow label="（道具の詳細）" value={b.tools_other} />
          )}
        </div>
      </div>

      {/* ② 喫煙 */}
      <NumItem num="②" label="喫煙習慣">
        <CheckChip checked={d.smoking === "none"} strong>吸わない</CheckChip>
        <CheckChip checked={d.smoking === "yes"}>吸う</CheckChip>
        <CheckChip checked={d.smoking === "past"}>過去に吸っていた</CheckChip>
      </NumItem>

      {/* ③ 睡眠 */}
      <NumItem num="③" label="睡眠時間">
        <FillLine
          value={d.sleep_hours != null && (d.sleep_hours as unknown) !== "" ? `${d.sleep_hours} 時間` : ""}
          placeholder="—"
          width={140}
        />
      </NumItem>

      {/* ④ 飲み物 */}
      <NumItem num="④" label="習慣的によく飲むもの">
        <CheckChip checked={dr.has_drinks === "none"} strong>特になし</CheckChip>
        <CheckChip checked={drinksYes}>あり</CheckChip>
        {drinksYes && <SubFillRow label="（飲み物）" value={dr.name} />}
      </NumItem>

      {/* ⑤ 間食 */}
      <NumItem num="⑤" label="間食">
        <CheckChip checked={d.snacking === "none"} strong>しない</CheckChip>
        <CheckChip checked={d.snacking === "regular"}>規則正しく摂る</CheckChip>
        <CheckChip checked={d.snacking === "irregular"}>不規則に摂る</CheckChip>
      </NumItem>
    </>
  );
}

function Section6({ data }: { data?: QuestionnaireJsonData["complaint6"] }) {
  const treatments = data?.treatments ?? [];
  return (
    <>
      <SectionHead num="６" title="ご希望の治療について" note="あてはまるものすべて" />
      <div style={{ display: "flex", flexDirection: "column", gap: 4, padding: "10px 0" }}>
        {TREATMENT_LIST.map((t) => (
          <CheckChip key={t} checked={has(treatments, t)}>{t}</CheckChip>
        ))}
      </div>
    </>
  );
}

// ==================== メイン ====================
export interface QuestionnaireDocumentProps {
  json: QuestionnaireJsonData | null | undefined;
  patientName?: string;
  meta?: { age?: number; gender?: string; birthDate?: string | null };
  filledDate?: string;
  compact?: boolean;
}

export function QuestionnaireDocument({
  json,
  patientName,
  meta,
  filledDate,
  compact = false,
}: QuestionnaireDocumentProps) {
  if (!json) return null;
  return (
    <QuestionnaireCompactContext.Provider value={compact}>
      <div
        style={{
          maxWidth: 960,
          margin: "0 auto",
          background: Q_COLORS.paper,
          border: `1px solid ${Q_COLORS.paperLine}`,
          borderRadius: 12,
          padding: compact ? "22px 16px 26px" : "36px 44px 44px",
          boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
          fontFamily: Q_FONT,
          color: Q_COLORS.ink,
          letterSpacing: "0.02em",
          overflowX: "hidden",
        }}
      >
        {/* Header */}
        <header
          style={{
            display: "flex",
            flexDirection: compact ? "column" : "row",
            justifyContent: "space-between",
            alignItems: compact ? "flex-start" : "flex-end",
            gap: compact ? 10 : 16,
            paddingBottom: 14,
            marginBottom: 8,
            borderBottom: `2px solid ${Q_COLORS.paperLine}`,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 10,
                color: Q_COLORS.accent,
                fontWeight: 700,
                letterSpacing: "0.2em",
                marginBottom: 4,
              }}
            >
              MEDICAL INTERVIEW SHEET
            </div>
            <div style={{ fontSize: compact ? 20 : 22, fontWeight: 800, letterSpacing: "0.04em" }}>
              問 診 票
            </div>
          </div>
          <div
            style={{
              textAlign: compact ? "left" : "right",
              fontSize: 12,
              color: Q_COLORS.inkMute,
              lineHeight: 1.7,
            }}
          >
            {patientName && (
              <div style={{ fontSize: 14, fontWeight: 700, color: Q_COLORS.ink }}>
                {patientName}
              </div>
            )}
            {meta && (
              <>
                {meta.birthDate && <div>生年月日 {formatBirthDateForDisplay(meta.birthDate)}</div>}
                <div>
                  {meta.age != null && `${meta.age}歳`}
                  {meta.gender && `・${meta.gender}`}
                </div>
              </>
            )}
            {filledDate && <div style={{ fontSize: 11 }}>記入日 {filledDate}</div>}
          </div>
        </header>

        <div style={{ paddingTop: 4 }}>
          <Section1 data={json.complaint1} />
          <Section2 data={json.complaint2} />
          <Section3 data={json.complaint3} />
          <Section4 data={json.complaint4} />
          <Section5 data={json.complaint5} />
          <Section6 data={json.complaint6} />
        </div>

        {/* Footer */}
        <div
          style={{
            marginTop: 28,
            paddingTop: 12,
            borderTop: `1px solid ${Q_COLORS.paperLine}`,
            display: "flex",
            flexDirection: compact ? "column" : "row",
            gap: compact ? 4 : 12,
            justifyContent: "space-between",
            fontSize: 10,
            color: Q_COLORS.inkFade,
            letterSpacing: "0.08em",
          }}
        >
          <span>以上の内容に相違ありません。</span>
          <span>FORM.INT-2025</span>
        </div>
      </div>
    </QuestionnaireCompactContext.Provider>
  );
}
